/**
 * Session keep-alive service. Listens for user activity and pings the
 * keep-alive endpoint with a 2-min initial delay and 15-min throttle.
 * URL comes from config (not API response) so it works even when the
 * main API call fails. All fetch errors are silently swallowed.
 */

import type {Partition} from "@common-core/web-component-runtime/runtime/Partition";

const INITIAL_DELAY_MS = 2 * 60 * 1_000; // 2 minutes before first eligible request
const THROTTLE_INTERVAL_MS = 15 * 60 * 1_000; // minimum gap between requests

/**
 * Manages session keep-alive requests for authenticated users.
 * Instantiate once after RuntimeContext is available; call destroy() on disconnect.
 */
export class KeepAliveService {
  private readonly keepAliveUrl: string; // base endpoint URL from RuntimeContext
  private readonly partition: Partition; // deployment partition (e.g. "prod", "non-prod")
  private readonly initTime: number; // timestamp at construction for initial delay
  private lastKeepAliveTime: number | null = null; // last successful request timestamp
  private readonly boundActivityHandler: () => void; // stored for removeEventListener
  private readonly activityTarget: EventTarget; // resolved once so destroy() detaches from the same node

  /**
   * Starts listening for keydown/mousedown.
   * Falls back to `document` when `document.body` is not yet available
   * (events bubble up to it), so construction never throws.
   * @param keepAliveUrl - base keep-alive endpoint URL
   * @param partition - deployment partition from RuntimeContext
   */
  constructor(keepAliveUrl: string, partition: Partition) {
    this.keepAliveUrl = keepAliveUrl;
    this.partition = partition;
    this.initTime = Date.now();
    this.boundActivityHandler = this.handleActivity.bind(this);
    this.activityTarget = document.body ?? document;

    this.activityTarget.addEventListener("keydown", this.boundActivityHandler);
    this.activityTarget.addEventListener(
      "mousedown",
      this.boundActivityHandler
    );
  }

  /** Removes all event listeners. Call in disconnectedCallback. */
  destroy(): void {
    this.activityTarget.removeEventListener(
      "keydown",
      this.boundActivityHandler
    );
    this.activityTarget.removeEventListener(
      "mousedown",
      this.boundActivityHandler
    );
  }

  /** Appends ?partition=NON_PROD for non-prod environments. */
  private buildRequestUrl(): string {
    if (this.partition !== "prod") {
      return `${this.keepAliveUrl}?partition=NON_PROD`;
    }
    return this.keepAliveUrl;
  }

  /** Checks timing constraints and fires keep-alive if eligible. */
  private handleActivity(): void {
    const now = Date.now();

    if (now - this.initTime < INITIAL_DELAY_MS) {
      return; // still in 2-min grace period
    }

    if (
      this.lastKeepAliveTime !== null &&
      now - this.lastKeepAliveTime < THROTTLE_INTERVAL_MS
    ) {
      return; // throttled — last request too recent
    }

    void this.sendKeepAlive(now);
  }

  /**
   * Sends the keep-alive GET request with credentials.
   * Updates lastKeepAliveTime only on success so network failures
   * don't lock out retries for the next 15 minutes.
   */
  private async sendKeepAlive(requestTime: number): Promise<void> {
    try {
      await fetch(this.buildRequestUrl(), {credentials: "include"});
      this.lastKeepAliveTime = requestTime;
    } catch {
      // AC-6.6: silent failure — don't update timestamp so next event can retry
    }
  }
}
